import * as Yup from "yup";

export const getProviderId = () => {
  //get from local storage
  const providerData = JSON.parse(localStorage.getItem("providerDetails") || "{}");
  return providerData?.provider?.provider_id || "";
}

export const getPartnerId = () => {
  //get from local storage
  const providerData = JSON.parse(localStorage.getItem("providerDetails") || "{}");
  return providerData?.provider?.partnerId || "";
}

// Form initial values
export const initialValues = {
  // Service Information
  serviceInformation: {
    providerId: getProviderId() || "",
    serviceCategory: "",
    serviceName: "",
    serviceId: null,
    serviceCode: "",
    serviceShortDescription: "",
    serviceDetailedDescription: "",
    serviceType: "",
    duration: "",
    needType: "",
    subServiceIds: [],
    merchandiseIds: [],
    singleSubServiceSelection: false,
    singleMerchandiseSelection: false,
    availability: true,
  },
  // Pricing
  pricingInformation: {
    items: [],
    insuranceSupport: true,
    additionalFee: 0,
    acceptedPaymentMethod: "online",
  },
  // Location & Media
  locationAndMedia: {
    locations: [],
  },
};

// Validation schema
export const validationSchema = Yup.object().shape({
  serviceInformation: Yup.object().shape({
    serviceName: Yup.string().required('Service Name is required'),
    serviceId: Yup.number().nullable(),
    serviceCode: Yup.string().required("Service code is required"),
    serviceShortDescription: Yup.string()
      .required("Short description is required")
      .min(3, "Short description must be at least 3 characters")
      .max(200, "Short description cannot exceed 100 characters"),
    serviceDetailedDescription: Yup.string()
      .required("Detailed description is required")
      .min(3, "Detailed description must be at least 3 characters")
      .max(500, "Detailed description must be at most 300 characters"),
    providerId: Yup.string().required("Provider ID is required"),
    serviceCategory: Yup.string().required("Service category is required"),
    duration: Yup.string().max(300, "Duration must be at most 100 characters"),
    availability: Yup.boolean().required("Availability is required"),
  }),
  pricingInformation: Yup.object({
    items: Yup.array().of(
      Yup.object({
        selected: Yup.boolean(),
        type: Yup.string(),
        price: Yup.number()
          .when(['selected', 'type'], {
            is: (selected: boolean, type: string) => selected && type === 'Service',
            then: (schema) => schema
              .required('Price is required')
              .test(
                'price-range',
                'Price must be within allowed range',
                function (value: number | null | undefined) {
                  const { minPrice, maxPrice } = this.parent;
                  if (value === null || value === undefined) {
                    return false;
                  }
                  return value >= minPrice && value <= maxPrice;
                }
              ),
          })
          .when(['selected', 'type'], {
            is: (selected: boolean, type: string) => selected && (type === 'Sub-service' || type === 'Merchandise'),
            then: (schema) => schema
              .test(
                'price-allow-empty-or-range',
                'Price is required unless main service is selected. If entered, must be within allowed range.',
                function (value: any) {
                  const { minPrice, maxPrice } = this.parent;
                  const context = this.options && this.options.context;
                  let mainSelected = false;
                  if (context && context.pricingInformation && Array.isArray(context.pricingInformation.items)) {
                    const main = context.pricingInformation.items.find((item: any) => item.type === 'Service');
                    mainSelected = !!(main && main.selected);
                  }
                  if (mainSelected) {
                    // Allow empty (null/undefined/"")
                    if (value === null || value === undefined || value === '') return true;
                    // If entered, must be in range
                    return value >= minPrice && value <= maxPrice;
                  } else {
                    // Required and must be in range
                    return value !== null && value !== undefined && value !== '' && value >= minPrice && value <= maxPrice;
                  }
                }
              ),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
      })
    ),
    additionalFee: Yup.number()
      .positive()
      .min(0, "Additional fee must be 0 or greater")
      .max(999999, "Additional fee can't exceed 999999")
       .test(
        "is-decimal",
        "Additional fee can have up to two decimal places",
        value => value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString())
      ),
    insuranceSupport: Yup.boolean().required(),
    acceptedPaymentMethod: Yup.string()
  }),
  locationAndMedia: Yup.object({
    locations: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().required("Location ID required"),
        })
      )
      .min(1, "At least one location required"),
  }),
});

const parseBooleanFlag = (value: any) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === "true") return true;
    if (normalizedValue === "false") return false;
  }
  return undefined;
};

export const resolveSingleSelectionFlag = (
  source: any,
  type: "subService" | "merchandise",
) => {
  const candidateKeys =
    type === "subService"
      ? [
          "singleSubServiceSelection",
          "isSingleSubServiceSelection",
          "isSubserviceSingleSelect",
          "isSubServiceSingleSelect",
          "subServiceSingleSelect",
          "singleSubService",
          "allowOnlyOneSubService",
          "allowSingleSubServiceSelection",
          "single_sub_service_selection",
          "is_single_sub_service_selection",
          "sub_service_single_select",
          "single_sub_service",
          "allow_only_one_sub_service",
        ]
      : [
          "singleMerchandiseSelection",
          "isSingleMerchandiseSelection",
          "isMerchandiseSingleSelect",
          "isMerchandiseSingleSelect",
          "merchandiseSingleSelect",
          "singleMerchandise",
          "allowOnlyOneMerchandise",
          "allowSingleMerchandiseSelection",
          "single_merchandise_selection",
          "is_single_merchandise_selection",
          "merchandise_single_select",
          "single_merchandise",
          "allow_only_one_merchandise",
        ];

  const candidateSources = [
    source,
    source?.service,
    source?.providerService,
    source?.data,
  ];

  for (const candidateSource of candidateSources) {
    for (const key of candidateKeys) {
      const parsedValue = parseBooleanFlag(candidateSource?.[key]);
      if (parsedValue !== undefined) {
        return parsedValue;
      }
    }
  }

  return false;
};

const fetchSelectedSubServiceAndWithUpdatedPrice = (values:any) => {
  // const selectedSubServiceIds = values?.serviceInformation?.subServiceIds || [];
  const subServiceWithPrice = values?.pricingInformation?.items || [];
  //need only id , price
  // const resultArray:any[] = [];
  // selectedSubServiceIds.forEach((name: string) => {
  //   const matchedItem = subServiceWithPrice.find(
  //     (item: any) => item.name === name && item.type === "Sub-service"
  //   );
  //   if (matchedItem) {
  //     resultArray.push({ id: matchedItem.id, price: matchedItem.price ? matchedItem.price : null });
  //   }
  // });
  // return resultArray;

  // Only include selected sub-services
    return subServiceWithPrice
    .filter((item: any) => item.type === "Sub-service" && item.selected)
    .map((item: any) => ({ id: item.id, price: item.price }));
}

const fetchSelectedMerchandiseWithUpdatedPrice = (values:any) => {
  // const selectedMerchandiseIds = values?.serviceInformation?.merchandiseIds || [];
  const merchandiseWithPrice = values?.pricingInformation?.items || [];
  //need only id, price
  // const resultArray:any[] = [];
  // selectedMerchandiseIds.forEach((name:string) => {
  //   const matchedItem = merchandiseWithPrice.find(
  //     (item: any) => item.name === name && item.type === "Merchandise"
  //   );
  //   if (matchedItem) {
  //     resultArray.push({ id: matchedItem.id, price: matchedItem.price ? matchedItem.price : null });
  //   }
  // });
  // return resultArray;
  // Only include selected merchandise
  return merchandiseWithPrice
    .filter((item: any) => item.type === "Merchandise" && item.selected)
    .map((item: any) => ({ id: item.id, price: item.price }));
}

const getMainServicePrice = (values: any, serviceId: number) => {
 const pricingItems = values?.pricingInformation?.items || [];
 const mainServiceItem = pricingItems.find(
   (item: any) => item.type === "Service" && item.id === serviceId
 );
 return mainServiceItem ? mainServiceItem.price : 0;
}

export const formatPayload = (values: any,serviceId?: number | null,) => {
  const NEED_TYPE_MAP: Record<string, string> = {
    "Immediate Need": "at_need",
    "Preneed": "pre_need",
  };

  return {
    ...(serviceId ? { serviceId } : {}),
    categoryId: values.serviceInformation.categoryId,
    partnerId: getPartnerId(),
    isActive: values.serviceInformation.availability,
    shortDescription: values.serviceInformation.serviceShortDescription,
    detailDescription: values.serviceInformation.serviceDetailedDescription,
    needType: NEED_TYPE_MAP[values.serviceInformation.needType] || values.serviceInformation.needType,
    duration: values.serviceInformation.duration,
    additionalFee: values.pricingInformation.additionalFee,
    insuranceSupport: values.pricingInformation.insuranceSupport,
    acceptedPaymentMethod: values.pricingInformation.acceptedPaymentMethod,
    isSubserviceSingleSelect: values.serviceInformation.singleSubServiceSelection,
    isMerchandiseSingleSelect: values.serviceInformation.singleMerchandiseSelection,
    subServices: fetchSelectedSubServiceAndWithUpdatedPrice(values),
    merchandises: fetchSelectedMerchandiseWithUpdatedPrice(values),
    serviceableLocationIds: values.locationAndMedia.locations.map((loc: any) => loc.id),
    //servicePrice provide by provider for main service
    servicePrice: getMainServicePrice(values, values.serviceInformation.serviceId)
  };
};
   
